import { ElementProps } from '@/components/sections/builder/types';

import { FooterSectionType } from '@/components/sections/builder/elements/footer/footer/types';
import HeaderMenuSetting from '@/components/sections/builder/settings/header-menu-setting';
import { useMenu } from '@/hooks/api/storefront/menu-list';

import { getInnerText } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import Link from 'next/link';
import React from 'react';
import { shallow } from 'zustand/shallow';
import { Menu } from 'lucide-react';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import InputTab from '@/components/sections/builder/settings/input-tab';
import clsx from 'clsx';

const defaultData: FooterSectionType = {
  name: 'Menu',
  heading: 'Menu',
  menuId: '',
  menuLabel: '',
};

const resolveHref = (url_path: string) => {
  if (!url_path) return '#';

  const isExternal = /^https?:\/\//i.test(url_path);
  return isExternal ? url_path : `/${url_path.replace(/^\/+/, '')}`;
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as FooterSectionType;

  const { data: menuData } = useMenu(data.menuId, { enabled: !!data.menuId });
  const menuItems = menuData?.menu_items || [];

  return (
    <>
      <div
        className={clsx(
          'flex h-full w-full flex-col',
          data.menuListAlign === 'left' && '[&>*]:text-left',
          data.menuListAlign === 'center' && '[&>*]:text-center',
          data.menuListAlign === 'right' && '[&>*]:text-right'
        )}
      >
        <h2
          className='mb-1 text-lg font-medium '
          dangerouslySetInnerHTML={{ __html: data.heading || '' }}
        ></h2>
        <>
          {menuItems.map((menu) => {
            const isExternal = /^https?:\/\//i.test(menu.url_path || '');

            return (
              <div className='mb-1 w-full ' key={menu.title}>
                <Link
                  className='hover:underline'
                  href={resolveHref(menu.url_path || '')}
                  target={isExternal ? '_blank' : undefined}
                >
                  {menu.title}
                </Link>
              </div>
            );
          })}
        </>
      </div>
    </>
  );
};

export const ElementPreviewIcon = Menu;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as FooterSectionType;

  return (
    <div>
      <div className='mb-3'>
        <h3 className='mb-2 font-bold'>Heading</h3>
        <TextEditorMinimal
          value={data.heading as string}
          onChange={(val) => {
            builder.setChildData(element.parentId as string, element.id, {
              name: getInnerText(val as string),
              heading: val,
            });
          }}
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>
      <HeaderMenuSetting
        value={
          data.menuId
            ? { value: data.menuId as string, label: data.menuLabel as string }
            : undefined
        }
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            menuId: val?.value || '',
            menuLabel: val?.label || '',
          })
        }
      />
      <p className='mt-2'>Displays only top-level menu items.</p>
      <div className='mb-4 mt-4 font-bold'>Menu Alignment</div>
      <InputTab
        defaultValue={data.menuListAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            menuListAlign: val,
          });
        }}
      />
    </div>
  );
}

const FooterSectionMenu = {
  type: 'footer_section_menu',
  group: 'footer_section',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default FooterSectionMenu;
